import { useApp } from '@/contexts';

import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import {
  useGetMonitoringConfigQuery,
  useSaveMonitoringConfigMutation,
} from '@/features/monitoring/api/monitoringApi';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface EmailMonitoringProps {
  tenantId: string;
  envId: string;
}

/**
 * Email Monitoring / Notifications section
 * Matches dataProtect emailMonitoring.js - manage notification emails for recipe success/failure/partial success
 */
export const EmailMonitoring = ({ tenantId, envId }: EmailMonitoringProps) => {
  const { appId } = useApp();
  const [showManager, setShowManager] = useState(false);
  const [success, setSuccess] = useState(false);
  const [failure, setFailure] = useState(false);
  const [partialSuccess, setPartialSuccess] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newEmailList, setNewEmailList] = useState<string[]>([]);
  const [validationError, setValidationError] = useState('');

  const { data: config, isLoading } = useGetMonitoringConfigQuery(
    { tenantId, appId, environmentId: envId },
    { skip: !tenantId || !appId || !envId }
  );
  const [saveConfig, { isLoading: isSaving }] = useSaveMonitoringConfigMutation();

  // Sync local state from fetched config (404/error = no config, show empty)
  useEffect(() => {
    if (config?.id) {
      setSuccess(config.success ?? false);
      setFailure(config.failure ?? false);
      setPartialSuccess(config.partialSuccess ?? false);
      const emails = config.emailAddresses
        ? config.emailAddresses.split(',').filter((e) => e.trim())
        : [];
      setNewEmailList(emails);
    } else if (!isLoading) {
      setSuccess(false);
      setFailure(false);
      setPartialSuccess(false);
      setNewEmailList([]);
    }
  }, [config, isLoading]);

  const emails =
    config?.id && config.emailAddresses
      ? config.emailAddresses.split(',').filter((e) => e.trim())
      : [];

  const addNewEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (newEmailList.includes(trimmed)) {
      setValidationError('Email address already added');
      setTimeout(() => setValidationError(''), 3500);
      return;
    }
    if (EMAIL_REGEX.test(trimmed)) {
      setNewEmailList((prev) => [...prev, trimmed]);
      setNewEmail('');
    } else {
      setValidationError('Please enter a valid email address');
      setTimeout(() => setValidationError(''), 3500);
    }
  };

  const removeEmail = (email: string) => {
    setNewEmailList((prev) => prev.filter((e) => e !== email));
  };

  const handleSave = async () => {
    await saveConfig({
      tenantId,
      appId,
      environmentId: envId,
      ...(config?.id && { id: config.id }),
      emailAddresses: newEmailList.join(','),
      success,
      failure,
      partialSuccess,
    });
    setShowManager(false);
  };

  const handleManage = () => {
    setShowManager(true);
    setNewEmailList(emails);
    if (config?.id) {
      setSuccess(config.success ?? false);
      setFailure(config.failure ?? false);
      setPartialSuccess(config.partialSuccess ?? false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: 'bold', px: 0.75 }}>
          Notifications
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleManage}
          sx={{
            width: 115,
            fontWeight: 'bold',
            borderColor: '#6C63FF',
            color: '#6C63FF',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#5850d6',
              backgroundColor: 'rgba(108, 99, 255, 0.04)',
            },
          }}
        >
          Manage
        </Button>
      </Box>
      <Box sx={{ backgroundColor: '#EEE', width: '100%', mt: 2, borderRadius: 1 }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ width: '40%', verticalAlign: 'top', pt: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  Error Notification Emails
                </Typography>
              </TableCell>
              <TableCell sx={{ width: '60%', pt: 2 }}>
                {failure && emails.length > 0 ? (
                  emails.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {e}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No notifications set
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  Success Notification Emails
                </Typography>
              </TableCell>
              <TableCell sx={{ pt: 2 }}>
                {success && emails.length > 0 ? (
                  emails.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {e}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No notifications set
                  </Typography>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ verticalAlign: 'top', pt: 2, pb: 2 }}>
                <Typography variant="body2" fontWeight={500}>
                  Partial Success Notification Emails
                </Typography>
              </TableCell>
              <TableCell sx={{ pb: 2 }}>
                {partialSuccess && emails.length > 0 ? (
                  emails.map((e, i) => (
                    <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {e}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No notifications set
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      <Dialog open={showManager} onClose={() => {}} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Email Monitoring</DialogTitle>
        <DialogContent sx={{ backgroundColor: '#eee' }}>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Send email alert on:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox checked={success} onChange={() => setSuccess(!success)} size="small" />
                }
                label="Successful Jobs"
                sx={{ alignItems: 'flex-start' }}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={failure} onChange={() => setFailure(!failure)} size="small" />
                }
                label="Failed Jobs"
                sx={{ alignItems: 'flex-start' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={partialSuccess}
                    onChange={() => setPartialSuccess(!partialSuccess)}
                    size="small"
                  />
                }
                label="Partially Successful Jobs"
                sx={{ alignItems: 'flex-start' }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Recipient email addresses:
            </Typography>
            {newEmailList.length > 0 && (
              <Box component="ul" sx={{ listStyle: 'none', pl: 1, mb: 1 }}>
                {newEmailList.map((e, i) => (
                  <Box
                    component="li"
                    key={i}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderRadius: 0.5,
                      px: 0.5,
                    }}
                    onClick={() => removeEmail(e)}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                      {e}
                    </Typography>
                    <Typography variant="caption" color="error">
                      Remove
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
              <TextField
                size="small"
                placeholder="Add new email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNewEmail()}
                sx={{ width: 240 }}
              />
              <Button
                size="small"
                variant="contained"
                onClick={addNewEmail}
                disabled={!newEmail.trim()}
              >
                Add
              </Button>
            </Box>
            {validationError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {validationError}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" onClick={() => setShowManager(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
